import React, { useEffect, useRef, useState } from "react";
import { adminApi } from "../../lib/api";
import AdminLayout from "../../components/app/AdminLayout";
import { Card, Loader } from "./Dashboard";
import { Save, Check, Plus } from "lucide-react";

// Variables available in every template. Each maps to data we hold on an applicant
// (or organisation settings) and is replaced at send time.
const VAR_GROUPS = [
  {
    label: "Applicant",
    vars: [
      ["first_name", "Applicant's first name"],
      ["last_name", "Applicant's last name"],
      ["full_name", "Full name (First Last)"],
      ["email", "Email address"],
      ["phone", "Phone number"],
      ["gender", "Gender"],
      ["date_of_birth", "Date of birth"],
      ["age", "Age in years"],
      ["home_address", "Home address"],
      ["emergency_contact_name", "Emergency contact name"],
      ["emergency_contact_phone", "Emergency contact phone"],
      ["experience_level", "Experience level"],
      ["worked_previously", "Worked previously? (Yes/No)"],
      ["additional_info", "Anything else they told us"],
      ["status", "Current pipeline status"],
      ["application_id", "Short application reference"],
      ["availability_count", "Number of open dates selected"],
    ],
  },
  {
    label: "Organisation",
    vars: [
      ["org_name", "Your organisation name (Settings)"],
      ["org_email", "Your organisation contact email (Settings)"],
      ["site_url", "Public site URL (Settings)"],
      ["portal_url", "Actor portal login URL"],
    ],
  },
];

// Variables only injected for specific status emails.
const AUDITION_VARS = [
  ["audition_date_name", "Name of the audition date"],
  ["audition_date_full", "Full date e.g. Friday 17 October 2026"],
  ["audition_time", "Audition start–end time"],
  ["audition_location", "Audition location"],
];
const ROLE_VARS = [
  ["role_name", "The assigned role name e.g. Zombie 1"],
  ["character_name", "Character name from the role"],
  ["role_department", "Department / faction"],
  ["role_placement", "Placement e.g. End of corridor, Zone B"],
  ["event_name", "The event name"],
  ["character_sheet_url", "Public link to the character profile page"],
  ["role_accept_url", "Secure link to accept the role & sign the contract"],
];
const CONDITIONAL_VARS = {
  audition_offered: [["audition_confirm_url", "Unique one-time audition-confirmation link"]],
  audition_booked: [...AUDITION_VARS, ["roles_page_url", "Public link to the roles/characters page"]],
  offered_role: ROLE_VARS,
  contract_accepted: [
    ["character_sheet_url", "Direct link to the actor's assigned character / role sheet"],
    ["role_name", "Actor's assigned role name"],
    ["event_name", "The event name"],
    ["create_password_url", "Secure link for the actor to set up their portal password"],
  ],
  contracted: [
    ["create_password_url", "Secure one-time link for the actor to create their portal password"],
    ["password", "(Legacy) temporary portal password — no longer used"],
  ],
};

export default function Templates() {
  const [items, setItems] = useState(null);
  const [selected, setSelected] = useState(null);
  const [savedKey, setSavedKey] = useState(null);
  const subjectRef = useRef(null);
  const bodyRef = useRef(null);
  const lastFocused = useRef("body");

  const load = () => adminApi.get("/admin/email-templates").then(({ data }) => { setItems(data.items); if (data.items.length && !selected) setSelected(data.items[0].trigger); });
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const current = items?.find((t) => t.trigger === selected);

  const save = async () => {
    await adminApi.patch(`/admin/email-templates/${current.trigger}`, { subject: current.subject, body: current.body });
    setSavedKey(current.trigger);
    setTimeout(() => setSavedKey(null), 2000);
  };

  const updateCurrent = (k, v) => setItems((arr) => arr.map((t) => t.trigger === selected ? { ...t, [k]: v } : t));

  // Insert {{token}} at the caret of the last-focused field (subject or body).
  const insertVar = (name) => {
    const token = `{{${name}}}`;
    const field = lastFocused.current === "subject" ? "subject" : "body";
    const ref = field === "subject" ? subjectRef : bodyRef;
    const el = ref.current;
    const value = current?.[field] ?? "";
    if (!el) { updateCurrent(field, value + token); return; }
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    const next = value.slice(0, start) + token + value.slice(end);
    updateCurrent(field, next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + token.length;
      el.setSelectionRange(pos, pos);
    });
  };

  if (!items) return <AdminLayout title="Email Templates"><Loader/></AdminLayout>;

  const conditional = current ? (CONDITIONAL_VARS[current.trigger] || []) : [];

  return (
    <AdminLayout title="Email Templates" subtitle="Edit messages sent for each status change">
      <div className="grid lg:grid-cols-4 gap-4">
        <div className="lg:col-span-1 space-y-1">
          {items.map((t) => (
            <button key={t.trigger} onClick={() => setSelected(t.trigger)}
              data-testid={`tmpl-${t.trigger}`}
              className={`w-full text-left px-3 py-2.5 text-sm border ${selected === t.trigger ? "border-blood bg-blood/5" : "border-[#1a1a1a] hover:border-[#262626]"}`}>
              <div className="font-mono text-[10px] text-[#6B6862] uppercase tracking-wider">{t.trigger}</div>
              <div className="mt-0.5">{t.name}</div>
            </button>
          ))}
        </div>

        <div className="lg:col-span-3 space-y-4">
          {current && (
            <Card title={current.name} action={
              <button onClick={save} data-testid="tmpl-save-btn" className="flex items-center gap-2 px-3 py-1.5 text-xs uppercase tracking-wider bg-blood text-white hover:bg-[#FF3333]">
                {savedKey === current.trigger ? <Check size={12} /> : <Save size={12} />}
                {savedKey === current.trigger ? "Saved" : "Save"}
              </button>
            }>
              <div className="mb-4">
                <label className="label-mini block mb-1.5">Subject</label>
                <input data-testid="tmpl-subject" ref={subjectRef} value={current.subject}
                  onFocus={() => { lastFocused.current = "subject"; }}
                  onChange={(e) => updateCurrent("subject", e.target.value)}
                  className="w-full bg-[#050505] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none focus:border-blood" />
              </div>
              <div>
                <label className="label-mini block mb-1.5">Body (HTML supported)</label>
                <textarea data-testid="tmpl-body" ref={bodyRef} rows={16} value={current.body}
                  onFocus={() => { lastFocused.current = "body"; }}
                  onChange={(e) => updateCurrent("body", e.target.value)}
                  className="w-full bg-[#050505] border border-[#262626] px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-blood resize-none" />
              </div>
            </Card>
          )}

          {/* Variable reference — click any tag to insert it at your cursor */}
          <Card title="Insert Variables">
            <p className="text-xs text-[#A19D94] mb-4">
              Click a tag to insert it into the field you last clicked (Subject or Body). At send time each tag is
              replaced with the applicant's data. Empty values are rendered as blank.
            </p>
            {VAR_GROUPS.map((group) => (
              <div key={group.label} className="mb-4 last:mb-0">
                <div className="label-mini mb-2">{group.label}</div>
                <div className="flex flex-wrap gap-2">
                  {group.vars.map(([name, desc]) => (
                    <button
                      key={name}
                      type="button"
                      title={desc}
                      onClick={() => insertVar(name)}
                      data-testid={`var-${name}`}
                      className="group inline-flex items-center gap-1 border border-[#262626] hover:border-blood hover:bg-blood/5 px-2 py-1 transition-colors"
                    >
                      <Plus size={11} className="text-[#6B6862] group-hover:text-blood" />
                      <span className="font-mono text-[11px] text-[#F2EBE1] group-hover:text-blood">{`{{${name}}}`}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}

            {conditional.length > 0 && (
              <div className="mb-0 pt-3 mt-1 border-t border-[#1a1a1a]">
                <div className="label-mini mb-2 text-blood">Only on this email</div>
                <div className="flex flex-wrap gap-2">
                  {conditional.map(([name, desc]) => (
                    <button
                      key={name}
                      type="button"
                      title={desc}
                      onClick={() => insertVar(name)}
                      data-testid={`var-${name}`}
                      className="group inline-flex items-center gap-1 border border-blood/40 hover:border-blood bg-blood/5 px-2 py-1 transition-colors"
                    >
                      <Plus size={11} className="text-blood" />
                      <span className="font-mono text-[11px] text-blood">{`{{${name}}}`}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}
